import { Inject, Injectable, Optional } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
} from "@angular/router";

import type * as express from "express";
import { Observable, of } from "rxjs";
import {
    catchError,
    filter,
    first,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { Product } from "@hermes/api-model-product";
import { Context, REQUEST, RESPONSE, StorageService } from "@hermes/app-core";
import { ProductAddToCartService } from "@hermes/fragments/product-add-to-cart";
import { Logger } from "@hermes/logger";
import { ProductPageFacade } from "@hermes/states/product-page";
import { redirectResponse } from "@hermes/utils/services/response";
import { urlSkuToSku } from "@hermes/utils-generic/helpers";
import { RouterService } from "@hermes/utils-generic/services/router";
import { LOGGER } from "@hermes/web-logger";

@Injectable()
export class ProductPageGuard implements CanActivate {
    constructor(
        private productPageFacade: ProductPageFacade,
        @Optional() @Inject(REQUEST) private request: express.Request,
        @Optional() @Inject(RESPONSE) private response: express.Response,
        private context: Context,
        private router: Router,
        private routerService: RouterService,
        private storageService: StorageService,
        @Inject(LOGGER) private logger: Logger,
        private productAddToCartService: ProductAddToCartService,
    ) {}

    /**
     * @returns true if there is a fetched product corresponding to requested sku in product page state
     */
    public canActivate(
        route: ActivatedRouteSnapshot,
        _state: RouterStateSnapshot,
    ): Observable<boolean> {
        this.addBackInStockparamsInState(route);
        const urlSku = this.getSkuParam(route);
        const urlSlug = this.getSlugParam(route);
        const isVariant: boolean =
            this.router.getCurrentNavigation()?.extras?.state?.["variant"];

        return this.getFromStoreOrFetchProduct(urlSku, isVariant).pipe(
            tap((product: Product) => {
                if (product.slug === urlSlug) {
                    return;
                }
                // if server mode and slug is not product slug, do a 301 redirect to correct url, for SEO
                if (this.context.isInServerMode()) {
                    this.logger.info(
                        `Slug in url ${urlSlug} is not matching product slug ${product.slug}. Redirect to ${product.url}`,
                    );
                    redirectResponse({
                        request: this.request,
                        response: this.response,
                        newPath: product.url,
                    });
                }
                // if browser mode and slug is not product slug, do a spa redirect to correct url, for SEO
                if (this.context.isInBrowserMode()) {
                    this.routerService.spaRedirect(product.url);
                }
            }),
            switchMap(() => of(true)),
            catchError(() => of(false)),
        );
    }

    /**
     * Return sku to fetch from loaded route
     *
     * @param route snapshot of route to get params
     */
    public getSkuParam(route: ActivatedRouteSnapshot): string {
        const { slug, skuPart } = route.params;
        let urlSku: string = slug.split("-").pop();
        // manage case of sku with / in it
        if (skuPart) {
            urlSku = `${urlSku}/${skuPart}`;
        }
        return urlSkuToSku(urlSku);
    }

    /**
     * Return slug to fetch from loaded route
     *
     * @param route snapshot of route to get params
     */
    public getSlugParam(route: ActivatedRouteSnapshot): string {
        const { slug } = route.params;
        const slugParts: string[] = slug.split("-");
        // remove sku
        slugParts.pop();
        return slugParts.join("-");
    }

    private addBackInStockparamsInState(url: ActivatedRouteSnapshot): void {
        if (
            this.context.isInBrowserMode() &&
            url.queryParams?.utm_campaign?.toLowerCase() === "backinstock"
        ) {
            this.storageService.setSessionStorageItem(
                "backInStockFromEmailNotification",
                true,
            );
            this.storageService.setSessionStorageItem(
                "productSkuBackInStock",
                url.queryParams.utm_content,
            );
        }
    }

    /**
     * Return an Observable of requested product from state or from DAC
     *
     * @param urlSku product's sku to fetch
     * @param isVariant coming from a variation of the product
     */
    private getFromStoreOrFetchProduct(
        urlSku: string,
        isVariant: boolean,
    ): Observable<Product> {
        return this.productPageFacade.currentProduct$.pipe(
            switchMap((currentProduct: Product) => {
                if (currentProduct?.sku === urlSku) {
                    // If no product is fetched, ignore loading and only reset state
                    this.productAddToCartService.initAddToCartCallToAction(
                        currentProduct,
                        isVariant,
                    );

                    return of([currentProduct, false] as const);
                }

                this.productPageFacade.fetchProduct(urlSku, isVariant);
                return of(currentProduct).pipe(
                    withLatestFrom(this.productPageFacade.isProductLoading$),
                );
            }),
            filter(
                ([currentProduct, isLoading]) =>
                    currentProduct !== undefined && !isLoading,
            ),
            map(([currentProduct]) => currentProduct),
            first(),
        );
    }
}
